import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 overflow-hidden">
      {/* Header */}
      <motion.header
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 p-4 sm:p-6"
      >
        <nav className="flex justify-between items-center max-w-7xl mx-auto">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="text-3xl font-bold font-kid flex items-center gap-2 shrink-0"
            style={{ color: 'rgb(3, 59, 108)' }}
          >
            <img src="/images/logo.png" alt="Logo" className="w-9 h-9 sm:w-10 sm:h-10 object-contain drop-shadow-md shrink-0" />
            {/* 🟢 手机端隐藏字标：下面 hero 里就是一个巨大的 VOCAB RUN，
                这里再放一遍会把三个按钮挤到 logo 上（320px 宽根本放不下） */}
            <span className="hidden sm:inline">VOCAB RUN</span>
          </motion.div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            {/* 🟢 [新增] 访客试玩入口 */}
            <Link
              to="/try"
              className="bg-sky-500 hover:bg-sky-600 text-white px-3 sm:px-6 py-2 rounded-full transition-all duration-200 font-semibold text-sm sm:text-base whitespace-nowrap"
            >
              Try it free
            </Link>
            <Link
              to="/login"
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-gray-800 px-3 sm:px-6 py-2 rounded-full transition-all duration-200 text-sm sm:text-base whitespace-nowrap"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="bg-white text-sky-500 hover:bg-gray-100 px-3 sm:px-6 py-2 rounded-full transition-all duration-200 font-semibold text-sm sm:text-base whitespace-nowrap"
            >
              Register
            </Link>
          </div>
        </nav>
      </motion.header>

      {/* Hero Section */}
      <div className="relative max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-16">
          <motion.h1
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-6xl md:text-8xl font-bold mb-6 font-kid flex justify-center items-center gap-4"
            style={{ color: 'rgb(3, 59, 108)' }}
          >
            <img src="/images/logo.png" alt="Vocab Run Logo" className="w-20 h-20 md:w-28 md:h-28 object-contain drop-shadow-lg" />
            VOCAB RUN
          </motion.h1>
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-2xl md:text-3xl text-gray-800 mb-8 font-semibold"
          >
            Build Your Vocabulary Through Illustrations
          </motion.p>
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            {/* 🟢 [新增] 试玩是主 CTA：不注册也能立刻玩 */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/try"
                className="bg-sky-500 text-white hover:bg-sky-600 px-6 sm:px-8 py-4 rounded-full text-lg sm:text-xl font-bold transition-all duration-200 transform hover:scale-105 shadow-2xl whitespace-nowrap"
              >
                Try it now — no signup
              </Link>
              <Link
                to="/register"
                className="bg-white text-sky-500 hover:bg-gray-100 px-6 sm:px-8 py-4 rounded-full text-lg sm:text-xl font-bold transition-all duration-200 shadow-lg whitespace-nowrap"
              >
                Start Your Adventure!
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Feature Cards */}
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          <motion.div
            whileHover={{ scale: 1.05, y: -10 }}
            className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-center text-gray-800"
          >
            <div className="text-4xl mb-4">📚</div>
            <h3 className="text-xl font-bold mb-2">Courses</h3>
            <p className="text-sm text-gray-700">Structured learning paths from Core to Genius level</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, y: -10 }}
            className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-center text-gray-800"
          >
            <div className="text-4xl mb-4">📖</div>
            <h3 className="text-xl font-bold mb-2">Vocabulary Library</h3>
            <p className="text-sm text-gray-700">Comprehensive word collection with definitions</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, y: -10 }}
            className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-center text-gray-800"
          >
            <div className="text-4xl mb-4">🎮</div>
            <h3 className="text-xl font-bold mb-2">Games</h3>
            <p className="text-sm text-gray-700">Fun interactive games to reinforce learning</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, y: -10 }}
            className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-center text-gray-800"
          >
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-xl font-bold mb-2">Progress</h3>
            <p className="text-sm text-gray-700">Track your learning journey and achievements</p>
          </motion.div>
        </motion.div>

        {/* Running Characters */}
        <motion.div
          initial={{ x: -200, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 1.5, delay: 1.2 }}
          className="relative"
        >
          <div className="flex justify-center items-end space-x-6 mb-8">
            {/* Characters */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0 }}
              className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden"
            >
              <img 
                src="/images/character-1.png" 
                alt="Running person" 
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
              className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden"
            >
              <img 
                src="/images/character-2.png" 
                alt="Running person" 
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
              className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden"
            >
              <img 
                src="/images/character-3.png" 
                alt="Running person" 
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
              className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden"
            >
              <img 
                src="/images/character-4.png" 
                alt="Running person" 
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.8 }}
              className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden"
            >
              <img 
                src="/images/character-5.png" 
                alt="Running person" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Features List */}
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8 text-gray-800"
        >
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">Why Choose VOCAB RUN?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <div className="text-2xl">🎯</div>
              <div>
                <h3 className="font-bold mb-2">Adaptive Learning</h3>
                <p className="text-sm text-gray-700">Progress from Core to Stretch to Genius levels at your own pace</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-2xl">🏆</div>
              <div>
                <h3 className="font-bold mb-2">Achievement System</h3>
                <p className="text-sm text-gray-700">Earn badges and points as you master new vocabulary</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-2xl">👨‍🏫</div>
              <div>
                <h3 className="font-bold mb-2">Teacher Support</h3>
                <p className="text-sm text-gray-700">Teachers can assign custom vocabulary lists and track progress</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-2xl">🎮</div>
              <div>
                <h3 className="font-bold mb-2">Gamified Experience</h3>
                <p className="text-sm text-gray-700">Learn through exciting games and interactive challenges</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="text-center text-gray-800 py-8"
      >
        <p className="text-lg">Ready to start your vocabulary adventure?</p>
        <Link
          to="/register"
          className="inline-block mt-4 bg-white text-sky-500 hover:bg-gray-100 px-8 py-3 rounded-full font-bold transition-all duration-200 transform hover:scale-105"
        >
          Join VOCAB RUN Today!
        </Link>
        <div className="mt-8 flex justify-center items-center gap-4">
          <span className="text-sm font-semibold text-gray-700">Follow us</span>
          <a
            href="https://www.instagram.com/vocab_run?igsh=MWloMDAyYnZpYmJycg=="
            target="_blank"
            rel="noopener noreferrer"
            aria-label="VOCAB RUN on Instagram"
            className="bg-white bg-opacity-60 hover:bg-opacity-100 text-gray-800 p-3 rounded-full shadow transition-all duration-200 transform hover:scale-110"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>
          </a>
          <a
            href="https://www.tiktok.com/@vocab.run?_r=1&_t=ZG-985Teo1zyW"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="VOCAB RUN on TikTok"
            className="bg-white bg-opacity-60 hover:bg-opacity-100 text-gray-800 p-3 rounded-full shadow transition-all duration-200 transform hover:scale-110"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
            </svg>
          </a>
        </div>
      </motion.footer>
    </div>
  );
};

export default HomePage;
